import { AcumaticaLoadingOptions } from '../loading-indicator/AcumaticaLoadingOptions';
import { InventorySummaryDetail } from './InventorySummaryInquiry.service';
import { Adjustment, AdjustmentDetail, AdjustmentService } from './Adjustment.service';
import { ConfirmationDialog } from '../../common/confirmation-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { AcumaticaError, CancelActionError, NotFoundError } from '../acumatica-errors';
import { SalesOrderService, SalesOrder, SalesOrderShipment } from './SalesOrder.service';
import { AcumaticaRequestOptions, AcumaticaService } from '../acumatica.service';
import { AcumaticaEntity, AcumaticaEntityService, GenericEntityService, GenericInquiryService } from './acumatica-entity';
import { Component, EventEmitter, Injectable, Output } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { Acumatica, AcumaticaFieldFormat } from '../acumatica-field-decorator';
import { SafeResourceUrl } from '@angular/platform-browser';
import { Transfer, TransferDetail, TransferService } from './TransferOrder.service';
import { AlertDialog } from 'src/app/common/alert-dialog/alert-dialog.component';
import { WarehouseSelectDialog } from '../warehouse-select-dialog/warehouse-select-dialog.component';
import { environment } from 'src/environments/environment';

@Injectable({
    providedIn: 'root'
})
export class ShipmentService extends AcumaticaEntityService {
    constructor(
        private dialog: MatDialog,
        protected acumatica: AcumaticaService,
        private salesOrders: SalesOrderService,
        private adjustments: AdjustmentService,
        private transfers: TransferService
    ) {
        super(acumatica, 'Shipment', Shipment);
    }

    get(
        ShipmentNbr: string,
        options: AcumaticaRequestOptions = {}
    ): Observable<Shipment> {
        options.noCache = true; // never cache Shipments

        return super.getEntity(ShipmentNbr, {
            ...{
                expand: ['Details', 'Details/Allocations'],
                loader: { message: "Getting Shipment" }
            },
            ...options,
        })
    }

    search(filter: string, options: AcumaticaRequestOptions = {}): Observable<Shipment[]> {
        return super.getEntities(filter, options);
    }

    put(shipment: Shipment, options?: AcumaticaRequestOptions): Observable<Shipment> {
        return new Observable<Shipment>(subscriber => {
            super.putEntity(shipment, options).subscribe(
                updatedShipment => {
                    subscriber.next(updatedShipment);
                    subscriber.complete();
                },
                err => {
                    subscriber.error(err);
                    subscriber.complete();
                }
            );
        });
    }

    createShipment(
        SOOrderType: string,
        SOOrderNbr: string,
        warehouseID: string | undefined = undefined,
        showLoadingIndicators: boolean = true
    ): Observable<SalesOrderShipment> {
        return new Observable<SalesOrderShipment>(subscriber => {
            let errorHandler = (error: AcumaticaError) => {
                if (error instanceof NotFoundError) error.message = "Invalid Sales Order Number";
                subscriber.error(error);
            };

            // get the sales order with current list of Shipments
            let getSOOpts: any = {
                select: ['OrderType', 'OrderNbr', 'Status', 'ShipmentDate'],
                expand: ['Shipments', 'Details'],
            };

            if (!showLoadingIndicators) getSOOpts.loader = null;

            this.salesOrders.get(SOOrderType, SOOrderNbr, getSOOpts, false)
                .subscribe((salesOrder: SalesOrder) => {
                    switch (salesOrder.Status) {
                        case 'Credit Hold':
                        case 'On Hold':
                            subscriber.error(new Error('Can not create Shipment\nSales Order Status is ' + salesOrder.Status));
                            return;
                    }

                    // check the SO for open Shipments
                    const openShipment = salesOrder.Shipments?.find(shipment => shipment.Status == 'Open');
                    if (openShipment) {
                        this.dialog.open(ConfirmationDialog, {
                            data: {
                                message: "There is already an open shipment for this Sales Order"
                                    + "\n\nOpen existing Shipment <em>" + openShipment.ShipmentNbr + "</em>?",
                                confirmText: 'Yes'
                            }
                        }).afterClosed().subscribe((openExisting: boolean) => {
                            if (openExisting)
                                subscriber.next(openShipment);
                            else
                                subscriber.error(new CancelActionError())
                        })
                        return;
                    }


                    // check if SO contains lines from multiple warehouses
                    // if so, dialog (async) prompt to select warehouse
                    (new Promise<string>((resolve, cancel) => {
                        if (warehouseID !== undefined) { resolve(warehouseID); return; }

                        let warehouseIDs = salesOrder.Details!
                            .filter(soline => !soline.Completed)
                            .map(soline => soline.WarehouseID)
                            .unique<string>();
                        if (warehouseIDs.length == 0) {
                            this.dialog.open(AlertDialog, {
                                data: {
                                    message: "There are no lines on the Order!"
                                }
                            });
                            cancel();
                        }
                        else if (warehouseIDs.length === 1) {
                            resolve(warehouseIDs[0]);
                        } else {
                            let dialogRef = this.dialog.open(WarehouseSelectDialog, {
                                data: {
                                    icon: 'Help',
                                    warehouseIDs: warehouseIDs,
                                    message: "Order contains lines from multiple warehouses.\nSelect Warehouse for Shipment:"
                                }
                            });
                            dialogRef.afterClosed().subscribe(response => {
                                if (response) resolve(response)
                                else cancel();
                            });
                        }
                    }))
                        .then(warehouseID => {
                            // have Acumatica create the Shipment
                            var today = new Date();

                            this.acumatica.action(
                                'SalesOrder', 'SalesOrderCreateShipment',
                                { "OrderType": salesOrder.OrderType, "OrderNbr": salesOrder.OrderNbr },
                                // TODO: SO ShipmentDate old, line remaining with ShipmentDate today -- can't ship, no items for picking
                                { "ShipmentDate": today > salesOrder.ShipmentDate ? today : salesOrder.ShipmentDate, "WarehouseID": warehouseID },
                                showLoadingIndicators ? { message: "Creating Shipment" } : null
                            ).subscribe(() => {
                                // CreateShipment action completed -- get SO again
                                this.salesOrders.get(SOOrderType, SOOrderNbr, {
                                    ...getSOOpts,
                                    ...{
                                        noCache: true,
                                        loader: showLoadingIndicators ? { message: "Getting Created Shipment Nbr" } : null
                                    }
                                }).subscribe((newSO: SalesOrder) => {
                                    // search for the Shipment that didn't exist in the SO previously
                                    let newShipment = newSO.Shipments!.find(shipment => {
                                        return salesOrder.Shipments!.find(prevShipment => {
                                            return prevShipment.ShipmentNbr == shipment.ShipmentNbr
                                        }) === undefined;
                                    });
                                    if
                                        (newShipment) subscriber.next(newShipment);
                                    else
                                        subscriber.error(new Error("It appears the Shipment failed to create"));
                                });
                            }, errorHandler);
                        })
                        .catch(() => {
                            subscriber.error(new CancelActionError());
                        });
                }, errorHandler)
        });
    }

    printShipmentConfirmation(shipmentNbr: string, loader?: AcumaticaLoadingOptions): Promise<void>;
    printShipmentConfirmation(shipment: Shipment, loader?: AcumaticaLoadingOptions): Promise<void>;
    printShipmentConfirmation(shipment: string | Shipment, loader: AcumaticaLoadingOptions | null = null): Promise<void> {
        const entity = {
            ShipmentType: 'Shipment',
            ShipmentNbr: (shipment instanceof Shipment) ? shipment.ShipmentNbr : shipment
        }
        return new Promise<void>((resolve, reject) => {
            this.acumatica.action('Shipment', 'PrintShipmentConfirmationToWhse', entity, null, loader)
                .subscribe(
                    () => { resolve(); },
                    error => reject(error)
                );
        });
    }

    setCurrentPickerID(shipment: Shipment, PickerID: string | null = this.acumatica.username) {
        // update the original object
        shipment.PickerID = PickerID;

        // create a small Shipment object to update Acumatica
        let updateShipment = new Shipment();
        updateShipment.Type = shipment.Type;
        updateShipment.ShipmentNbr = shipment.ShipmentNbr;
        updateShipment.PickerID = PickerID;

        this.acumatica.put('Shipment', updateShipment, { loader: null }).subscribe();
    }

    // #region Shipment Confirmation

    /**
     * Return a clone of a Shipment object with Details and Allocations
     * Used by confirmShipment to clone the shipment being confirmed to manipulate without causing UI binding issues
     * Used to serialize Shipment to local device storage
     */
    public cloneShipment(shipment: Shipment, cloneAllocations: boolean = true): Shipment {
        let clone = Object.assign(new Shipment(), shipment);
        clone.Details = [];
        let idxDetail = -1;
        shipment.Details.forEach(detail => {
            clone.Details.push(Object.assign(new ShipmentDetail(), detail));
            clone.Details[++idxDetail].Allocations = [];
            if (cloneAllocations) detail.Allocations.forEach(alloc => {
                clone.Details[idxDetail].Allocations.push(Object.assign(new ShipmentDetailAllocation(), alloc));
            });
        });

        return clone;
    }

    completeShipmentPicking(
        shipment: Shipment,
        PrinterID: string | undefined,
        isComplete: boolean,
        UpdateStatusCallback?: (status: CompleteShipmentStatus) => void,
    ): Observable<void> {
        return new Observable(subscriber => {
            // prevent modifications to shipment object causing UI binding issues
            let mainShipment = this.cloneShipment(shipment);

            // sort the lines back into Line Nbr order (from Location order)
            // necessary to add Allowances in proper sequence
            mainShipment.Details = mainShipment.Details.sort((a, b) => a.LineNbr! - b.LineNbr!);

            // initialize array for files to be uploaded
            let putFiles: [entityName: string, key: string | string[], file: any, filename?: string, options?: AcumaticaRequestOptions][] = [];

            // SaleOrders with an additional isModified boolean flag
            class ExtendedSalesOrder extends SalesOrder { isModified: boolean = false };

            // map of "SOOrderType/SOOrderNbr" to Promise<SalesOrder> entities
            const SalesOrderEntities: { [key: string]: Promise<ExtendedSalesOrder> } = {}
            const SalesOrders: { [key: string]: SalesOrder } = {}
            // Get a Sales Order entity from Acumatica as a Promise re-using same Promises
            const GetSalesOrder = (orderType: string, orderNbr: string, statusFeedback?: CompleteShipmentStatus): Promise<ExtendedSalesOrder> => {
                let key = orderType + '/' + orderNbr;
                if (SalesOrderEntities[key] == undefined) {
                    SalesOrderEntities[key] = new Promise<ExtendedSalesOrder>(resolve => {
                        if (statusFeedback) statusFeedback.Details = key;
                        this.salesOrders.get(orderType, orderNbr, { expand: 'Details', noCache: true, loader: null }).subscribe(so => {
                            SalesOrders[key] = so;
                            let modifiedSO = so as ExtendedSalesOrder;
                            modifiedSO.isModified = false;
                            resolve(modifiedSO);
                        }, error => { errHandler(error); return; })
                    });
                }
                return SalesOrderEntities[key];
            }

            // ensure Allocation modification detail types are set
            mainShipment.Details.forEach(detail => detail.Allocations.forEach(alloc => {
                alloc.EOR = !!alloc.EOR;
                alloc.EORQty = alloc.EOR ? (alloc.EORQty || 0) : 0;
                alloc.PCS = !!alloc.PCS;
                alloc.PCSQty = alloc.PCS ? (alloc.PCSQty || 0) : 0;
                alloc.FLA = !!alloc.FLA;
                alloc.FLAQty = alloc.FLA ? (alloc.FLAQty || 0) : 0;
            }));

            // #region status / feedback handling

            // function to handle errors from API processes relaying it to the status feedback indicator
            const errHandler = (error: any) => {
                status.error = error;
                UpdateStatusCallback && UpdateStatusCallback(status);
                subscriber.error({ error: error, status: status });
            }

            // initialize Shipment Status feedback object
            const status = new CompleteShipmentStatus(mainShipment);

            // images aren't copied to the clones shipment -- set that property from the original completeShipment object
            status.UploadImages =
                shipment.Details.filter(detail => detail.Allocations.filter(alloc => alloc.FLAImgs !== undefined && alloc.FLAImgs.length > 0).length > 0).length == 0
                    ? CompleteShipmentStepStatus.Omitted
                    : CompleteShipmentStepStatus.Pending;
            status.PrintingLabels = (PrinterID === undefined)
                ? CompleteShipmentStepStatus.Omitted
                : CompleteShipmentStepStatus.Pending;

            // print Pack List when shipment is [Complete] (not updating partial)
            // and only automatically for warehouse 03
            status.PrintPacklist = isComplete && mainShipment.WarehouseID == '03'
                ? CompleteShipmentStepStatus.Pending : CompleteShipmentStepStatus.Omitted;

            // relay updated events callback function
            status.updated.subscribe(() => UpdateStatusCallback && UpdateStatusCallback(status));

            // #endregion

            // #region Prepare Allowances (EOR, PCS, FLA)

            // initialize array for Allowance details (EOR, PCS, FLA) to be appended to SO
            let AddAllowances: {
                MainDetail: ShipmentDetail,
                MainAllocation: ShipmentDetailAllocation,
                AllowanceDetail?: ShipmentDetail,
                AllowanceAllocation?: ShipmentDetailAllocation,
                InventoryID: string,
                WarehouseID: string,
                Qty: number,
                IsFree: boolean,
                note: string,
            }[] = [];

            mainShipment.Details.forEach((detail, detailIdx) => {
                /* ACUMATICA BUG  (SH0306703)
                a shipment with multiple line details and multiple allocations for each was partially picked and updated and put in cut & hold
                the first line that was partially picked (300 of 500 was picked)
                the balance was received and order picking resumed for the shipment
                adding more picks to the partially picked first line and updating to Acumatica resulted in acumatica error
                -- item # with lot # has insufficient quantity available for shipping (even when set to qty == 0)
                removing the other lines from the Shipment PUT resolved or removing previously picked allocations from first line also resolved

                filtering to only picks that have a PickSource -- ones actively worked; not from previous partial picking -- should resolve this
                */
                // detail.Allocations = detail.Allocations.filter(alloc => alloc.PickSource);
                // BIG NOPE -- Memos / Cards don't have a PickSource

                detail.Allocations.forEach(alloc => {
                    // reduce the 'main' allocation quantity by variance allowances being converted to separate lines
                    alloc.Qty = alloc.Qty
                        - (alloc.EOR && alloc.EORQty || 0)
                        - (alloc.PCS && alloc.PCSQty || 0)
                        - (alloc.FLA && alloc.FLAQty || 0);

                    // End-Of-Roll
                    if (alloc.EOR && (alloc.EORQty || 0) != 0) {
                        AddAllowances.push({
                            MainDetail: detail,
                            MainAllocation: alloc,
                            InventoryID: detail.InventoryID,
                            WarehouseID: detail.WarehouseID,
                            Qty: alloc.EORQty!,
                            IsFree: alloc.EORFree,
                            note: "End of Roll Allowance - Line # " + detail.LineNbr
                        });
                    }

                    // Taking small Pieces gratis
                    if (alloc.PCS && (alloc.PCSQty || 0) != 0) {
                        AddAllowances.push({
                            MainDetail: detail,
                            MainAllocation: alloc,
                            InventoryID: detail.InventoryID,
                            WarehouseID: detail.WarehouseID,
                            Qty: alloc.PCSQty!,
                            IsFree: true,
                            note: "Small Piece Allowance - Line # " + detail.LineNbr
                        });
                    }

                    // Material Flaw
                    if (alloc.FLA && (alloc.FLAQty || 0) != 0) {
                        AddAllowances.push({
                            MainDetail: detail,
                            MainAllocation: alloc,
                            InventoryID: detail.InventoryID,
                            WarehouseID: detail.WarehouseID,
                            Qty: alloc.FLAQty!,
                            IsFree: true,
                            note: "Material Flaw - Line #" + detail.LineNbr
                        });

                        // upload image files to Shipment
                        if (alloc.FLAImgs !== undefined) {
                            alloc.FLAImgs.forEach((image, imageIndex) => {
                                let filename = "Flaw - Line " + detail.LineNbr;
                                if (alloc.FLAImgs.length > 1) {
                                    filename += " (" + (imageIndex + 1) + ")";
                                }
                                filename += image.file.name.substring(image.file.name.lastIndexOf('.'));
                                putFiles.push([
                                    'Shipment',
                                    mainShipment.ShipmentNbr,
                                    image.file,
                                    filename,
                                    { loader: null }
                                ]);
                            });
                        }
                    }
                })
            })

            // #endregion

            // #region Prepare Modify SO Liness (FULL)

            // iterate Shipment Details SO lines to be directly modified (FULL roll items)
            mainShipment.Details
                .filter(detail => detail.ShipFullRolls)
                .forEach(detail => {
                    // if there is a FULL roll qty
                    if (detail.PickedQty != 0) {
                        // Update the Shipment Ordered Qty
                        detail.OrderedQty = detail.PickedQty;

                        // Get the original Sales Order
                        let modifiedSalesOrderRef = GetSalesOrder(detail.OrderType, detail.OrderNbr);

                        // update the Promise object in the map to one resolved after the SO is updated
                        SalesOrderEntities[detail.OrderType + '/' + detail.OrderNbr] = new Promise<ExtendedSalesOrder>(resolve => {
                            // when the original SO is retrieved
                            modifiedSalesOrderRef.then(so => {
                                // find the Detail line
                                let soline = so.Details!.find(soline => soline.LineNbr == detail.OrderLineNbr)!;
                                // check if the shipped QTY is different
                                if (soline.OrderQty != detail.PickedQty + soline.QtyOnShipments) {
                                    // update the line
                                    soline.OrderQty = detail.PickedQty + soline.QtyOnShipments;
                                    // flag to update the SO in Acumatica
                                    so.isModified = true;
                                }
                                resolve(so);
                            });
                        });
                    }
                });
            // #endregion            

            // #region Prepare Bin Transfers
            let transferDocument = new Transfer();
            transferDocument.Description = "Shipment - " + mainShipment.ShipmentNbr;
            transferDocument.ToWarehouseID = transferDocument.FromWarehouseID = mainShipment.WarehouseID!;
            let transfers: string[] = [];

            mainShipment.Details.forEach(detail => {
                // iterate the allocations
                detail.Allocations
                    .filter(alloc => {
                        return alloc.PickSource
                            && (alloc.RestockQty > 0)
                            && (alloc.PickSource?.LocationID != null)
                            && (alloc.PickSource?.OriginalLocationID?.trim().toUpperCase() != alloc.PickSource?.LocationID?.trim().toUpperCase())
                    })
                    .forEach(alloc => {
                        // if reusing a piece and putting it away to a different location,
                        // duplicates in transfer document cause errors
                        // check for and prevent duplicate line details
                        let transferKey = [detail.InventoryID, alloc.LotSerialNbr, alloc.PickSource!.OriginalLocationID, alloc.PickSource!.LocationID].join("\t");
                        if (transfers.indexOf(transferKey) > -1) return;
                        transfers.push(transferKey);

                        // add the transfer line detail to the transfers document
                        let transfer = new TransferDetail();
                        transfer.InventoryID = detail.InventoryID;
                        transfer.FromLocationID = alloc.PickSource!.OriginalLocationID!;
                        transfer.ToLocationID = alloc.PickSource!.LocationID!;
                        transfer.Qty = alloc.RestockQty;
                        transfer.LotSerialNbr = alloc.LotSerialNbr;
                        transferDocument.Details.push(transfer);
                    });
            });
            // #endregion

            // #region API Acumatica Updates
            (async () => {

                // await (() => {
                //     let initializeAllowanceLinesShipment = this.cloneShipment(shipment, false);
                //     return this.put(initializeAllowanceLinesShipment).toPromise()
                // })();

                // 1. Create and Release the Inventory Adjustments
                // must be done first in case of increasing quantity on hand
                if (status.InventoryAdjustments == CompleteShipmentStepStatus.Pending) {
                    status.InventoryAdjustments = CompleteShipmentStepStatus.InProcess;
                    await (() => {
                        return new Promise<void>(adjustments_complete => {
                            if (mainShipment.adjustments.length > 0) {
                                console.log('Create & Release Adjustments');

                                let adjustmentDocument = new Adjustment();
                                adjustmentDocument.Description = "Shipment - " + mainShipment.ShipmentNbr;
                                adjustmentDocument.Details = mainShipment.adjustments
                                    .filter(adjustment => adjustment.Quantity != 0);

                                this.adjustments.put(adjustmentDocument, {
                                    //^ loader: "Adjusting Inventory"
                                    loader: null
                                }).subscribe(newAdjustment => {
                                    if (this.acumatica.disableAdjustmentRelease) {
                                        adjustments_complete();
                                    } else {
                                        this.adjustments.Release(newAdjustment, null).subscribe(() => {
                                            adjustments_complete();
                                        });
                                    }
                                }, error => { errHandler(error); return; });
                            }
                            else adjustments_complete();
                        });

                    })();

                    // clear adjusments collection in case of subsequent error & user trying to complete again
                    mainShipment.adjustments = [];
                    shipment.adjustments = [];
                    status.InventoryAdjustments = CompleteShipmentStepStatus.Complete;
                }

                // 2. Modify existing SO lines (FULL roll shipments)
                // does return the modified SO after PUT
                if (status.ModifySOLines == CompleteShipmentStepStatus.Pending) {
                    await (() => {
                        status.ModifySOLines = CompleteShipmentStepStatus.InProcess;
                        return new Promise<void>(solines_updated => {
                            let ListOfAllSOsBeingUpdated: Promise<SalesOrder | void>[] = [Promise.resolve()];
                            // iterate all the SOs in the map
                            Object.keys(SalesOrderEntities).forEach(key => {
                                let SOUpdatedPromise = SalesOrderEntities[key];

                                // update the Promise in the map to one resolved when the SO update is done
                                SalesOrderEntities[key] = new Promise<ExtendedSalesOrder>(resolve => {

                                    // after the SO is read and possibly modified by the FULL-roll initial handling above...
                                    SOUpdatedPromise.then(so => {
                                        // if it has actually changed (FULL roll quantity was adjusted)
                                        if (so.isModified) {
                                            // update Acumatica Sales Order
                                            status.Details = key;
                                            this.salesOrders.put(so, {
                                                expand: 'Details',
                                                // loader: "Updating SO Details\n" + key
                                                loader: null
                                            }).subscribe(so => {
                                                // clear FUL flag from detail lines (in case subsequent error and user tries to Complete again)
                                                mainShipment.Details.forEach(detail => {
                                                    //^ detail.Allocations
                                                    //^     .filter(alloc => alloc.FUL && (alloc.FULQty || 0) != 0)
                                                    //^     .forEach(alloc => { alloc.FUL = false; alloc.FULQty = 0 })
                                                    detail.ShipFullRolls = false;
                                                })

                                                // resolve promise with the SO
                                                resolve(so as ExtendedSalesOrder);
                                            }, error => { errHandler(error); return; });
                                        }
                                        else resolve(so);
                                    });

                                });

                                // wait for all SO updates to complete
                                ListOfAllSOsBeingUpdated.push(SalesOrderEntities[key])
                            });

                            Promise.all(ListOfAllSOsBeingUpdated).then(() => solines_updated());
                        });
                    })();
                    status.ModifySOLines = CompleteShipmentStepStatus.Complete;
                }

                // 3. Add new Allowance line details to the SO
                // added through Action so it does not return the modified SO
                if (status.AppendSOLines == CompleteShipmentStepStatus.Pending) {
                    await (() => {
                        status.AppendSOLines = CompleteShipmentStepStatus.InProcess;
                        return new Promise<void>(allowances_added => {
                            if (AddAllowances.length > 0) {
                                console.log('Add Allowance lines to SO');
                                let allowanceIdx = -1;
                                // define self-invoking function to add lines sequentially
                                let addNextItem = () => {
                                    if (++allowanceIdx < AddAllowances.length) {
                                        let allowance = AddAllowances[allowanceIdx];
                                        status.Details = allowance.MainDetail.OrderNbr + " - "
                                            + allowance.InventoryID + " x" + allowance.Qty + " " + allowance.MainDetail.UOM;
                                        this.salesOrders.addShippedItem(
                                            allowance.MainDetail.OrderType,
                                            allowance.MainDetail.OrderNbr,
                                            allowance.InventoryID,
                                            allowance.WarehouseID,
                                            allowance.Qty,
                                            allowance.IsFree,
                                            allowance.note,
                                            null
                                        ).subscribe(() => {
                                            // clear the flags from the allowance
                                            // (in case of subsequent error and user retries Complete)
                                            let originalAllocationDetail = shipment
                                                .Details.find(detail => detail.LineNbr == allowance.MainDetail.LineNbr)!
                                                .Allocations[allowance.MainDetail.Allocations.indexOf(allowance.MainAllocation)]
                                            originalAllocationDetail.EOR = false;
                                            originalAllocationDetail.EORQty = 0;
                                            originalAllocationDetail.PCS = false;
                                            originalAllocationDetail.PCSQty = 0;
                                            originalAllocationDetail.FLA = false;
                                            originalAllocationDetail.FLAQty = 0;
                                            originalAllocationDetail.FLAImgs = [];

                                            addNextItem();
                                        }, error => { errHandler(error); return; });
                                    } else {
                                        allowances_added();
                                    }
                                }
                                // add first line
                                addNextItem();
                            }
                            else allowances_added();
                        });
                    })();
                    status.AppendSOLines = CompleteShipmentStepStatus.Complete;
                }

                // 4. Get any SOs that had Allowance lines appended
                if (status.GetModifiedSOs == CompleteShipmentStepStatus.Pending) {
                    status.GetModifiedSOs = CompleteShipmentStepStatus.InProcess;
                    await (() => {
                        return new Promise<void>(all_modified_SOs_retrieved => {
                            let ListOfSORetrievalPromises: Promise<SalesOrder | void>[] = [Promise.resolve()];
                            let FetchedSOs: string[] = [];
                            for (let idx = 0; idx < AddAllowances.length; idx++) {
                                let allowance = AddAllowances[idx];
                                let key = allowance.MainDetail.OrderType + "/" + allowance.MainDetail.OrderNbr;
                                if (FetchedSOs.indexOf(key) == -1) {
                                    // unset the resolve Promise in the retrieved SOs map so it's subseqnetly retrieved
                                    delete SalesOrderEntities[key];
                                    // only retrieve each SO document once
                                    FetchedSOs.push(key);
                                    // Retrieve SO and add to list of promises to wait for
                                    ListOfSORetrievalPromises.push(
                                        GetSalesOrder(allowance.MainDetail.OrderType, allowance.MainDetail.OrderNbr, status)
                                    );
                                }
                            }

                            Promise.all(ListOfSORetrievalPromises).then(() => { all_modified_SOs_retrieved(); })
                        })
                    })();
                    status.GetModifiedSOs = CompleteShipmentStepStatus.Complete;
                }

                // 5. Prepare Shipment Allocations in Acumatica by creating records with zero quantities
                if (status.AppendShipmentLines == CompleteShipmentStepStatus.Pending) {
                    status.AppendShipmentLines = CompleteShipmentStepStatus.InProcess;

                    await (() => {
                        return new Promise<void>(shipmentAllocationInitializationComplete => {
                            let CopyRowIDs = (copyTo: any, copyFrom: any) => {
                                copyTo.__id = copyFrom.__id;
                                copyTo.__rowNumber = copyFrom.__rowNumber;
                                if (copyFrom.LineNbr !== undefined) copyTo.LineNbr = copyFrom.LineNbr;
                            }

                            // create an initialization Shipment object with ShipmentDetails and ShipmentDetailAllocations for each Pick
                            let initShipment = new Shipment();
                            initShipment.ShipmentNbr = mainShipment.ShipmentNbr;
                            let initDetail: ShipmentDetail;
                            let initAlloc: ShipmentDetailAllocation;
                            mainShipment.Details.forEach(detail => {
                                initShipment.Details.push(initDetail = new ShipmentDetail());
                                CopyRowIDs(initDetail, detail);

                                detail.Allocations.forEach(alloc => {
                                    initDetail.Allocations.push(initAlloc = new ShipmentDetailAllocation());
                                    CopyRowIDs(initAlloc, alloc);
                                    // existing picks/allocations will not be modified
                                    // new picks/allocations will be initialized to zero
                                    if (alloc.__rowNumber === undefined) {
                                        initAlloc.InventoryID = detail.InventoryID;
                                        initAlloc.Qty = 0;
                                    }
                                });
                            });

                            // create initial ShipmentDetail and ShipmentDetailAllocation records for new Allowance lines
                            for (let idx = 0; idx < AddAllowances.length; idx++) {
                                let allowance = AddAllowances[idx];
                                let so = SalesOrders[allowance.MainDetail.OrderType + '/' + allowance.MainDetail.OrderNbr];
                                let soline = so.Details![so.Details!.length - AddAllowances.length + idx];

                                // the new Allowance Line will need added to the Initialization shipment and the Main shipment both
                                initDetail = new ShipmentDetail();

                                mainShipment.Details.push(initDetail);
                                initShipment.Details.push(initDetail);

                                initDetail.InventoryID = allowance.InventoryID;
                                initDetail.OrderType = allowance.MainDetail.OrderType;
                                initDetail.OrderNbr = allowance.MainDetail.OrderNbr;
                                initDetail.OrderLineNbr = soline.LineNbr;
                                initDetail.LotSerialNbr = allowance.MainAllocation.LotSerialNbr;
                                initDetail.LocationID = allowance.MainAllocation.LocationID;
                                initDetail.FreeItem = true;
                                initDetail.Allocations.push(initAlloc = new ShipmentDetailAllocation());
                                initAlloc.InventoryID = allowance.InventoryID;
                                initAlloc.LocationID = allowance.MainAllocation.LocationID;
                                initAlloc.LotSerialNbr = allowance.MainAllocation.LotSerialNbr;
                                initAlloc.Qty = allowance.Qty;
                                allowance.AllowanceDetail = initDetail;
                            };

                            this.put(initShipment, { loader: null, expand: ['Details', 'Details/Allocations'] }).subscribe(updatedShipment => {
                                // copy the initialization row IDs to the corresponding ShipmentDetail and ShipmentDetailAllocations
                                updatedShipment.Details.forEach((detail, detail_idx) => {
                                    CopyRowIDs(mainShipment.Details[detail_idx], detail);
                                    detail.Allocations.forEach((alloc, alloc_idx) => {
                                        CopyRowIDs(mainShipment.Details[detail_idx].Allocations[alloc_idx], alloc);
                                    });
                                });

                                shipmentAllocationInitializationComplete();
                            }, error => { errHandler(error); return; });
                        });
                    })();

                    status.AppendShipmentLines = CompleteShipmentStepStatus.Complete;
                }

                // 6. Update the (main) Shipment document (with quantities)
                if (status.UpdateShipment == CompleteShipmentStepStatus.Pending) {
                    status.UpdateShipment = CompleteShipmentStepStatus.InProcess;

                    await (() => {
                        return new Promise<void>(shipment_updated => {
                            this.put(mainShipment, {
                                expand: ['Details', 'Details/Allocations'],
                                loader: null
                            }).subscribe(new_shipment => {
                                // Clean-up...
                                // Acumatica tends to create zero-quantity allocation lines on appended Allowances ?!
                                let bAnyZeroQtyAllocations: boolean = false;
                                new_shipment.Details.forEach(detail => {
                                    detail.Allocations.forEach(alloc => {
                                        if (alloc.Qty == 0) {
                                            (<any>alloc).__delete = true;
                                            bAnyZeroQtyAllocations = true;
                                        }
                                    });
                                });

                                if (bAnyZeroQtyAllocations) {
                                    this.put(new_shipment, { loader: null }).subscribe(new_shipment => {
                                        mainShipment = new_shipment;
                                        shipment_updated();
                                    });
                                } else {
                                    mainShipment = new_shipment;
                                    shipment_updated();
                                }
                            }, err => {
                                errHandler(err);
                                return;
                            });

                        });
                    })();
                    status.UpdateShipment = CompleteShipmentStepStatus.Complete;
                }

                // 7. Print the customer special Labels
                if (status.PrintingLabels == CompleteShipmentStepStatus.Pending) status.PrintingLabels = CompleteShipmentStepStatus.InProcess;
                await (() => {
                    if (PrinterID === undefined) {
                        return Promise.resolve();
                    } else {
                        return new Promise<void>(pin_tags_printed => {
                            return this.acumatica.action(
                                'Shipment', 'PrintCustomerSpecialTags',
                                mainShipment,
                                { DirectPrint: true, Printer: PrinterID },
                                null
                            ).subscribe(() => {
                                pin_tags_printed();
                            }, error => { errHandler(error); return; })
                        });
                    }
                })();

                if (status.PrintingLabels == CompleteShipmentStepStatus.InProcess) status.PrintingLabels = CompleteShipmentStepStatus.Complete;

                // 9. Print the Pack List
                if (status.PrintPacklist == CompleteShipmentStepStatus.Pending) {
                    status.PrintPacklist = CompleteShipmentStepStatus.InProcess;
                    await (() => {
                        return this.printShipmentConfirmation(mainShipment).then(() => {
                            status.PrintPacklist = CompleteShipmentStepStatus.Complete;
                        });
                    })();
                }

                // 10. Upload & attach photos
                if (status.UploadImages == CompleteShipmentStepStatus.Pending) {
                    status.UploadImages = CompleteShipmentStepStatus.InProcess;
                    for (let idx = 0; idx < putFiles.length; idx++) {
                        await (() => {
                            console.log('Attach photos');
                            status.Details = putFiles[idx][3] || null;
                            return new Promise<void>(file_uploaded => {
                                this.acumatica.putFile.apply(this.acumatica, putFiles[idx])
                                    .subscribe(
                                        () => { file_uploaded(); },
                                        error => { errHandler(error); return; }
                                    );
                            })
                        })();
                    }
                    status.UploadImages = CompleteShipmentStepStatus.Complete;
                }

                // 11. Create & release the Inventory Transfers
                if (status.InventoryTransfers == CompleteShipmentStepStatus.Pending) {
                    status.InventoryTransfers = CompleteShipmentStepStatus.InProcess;
                    await (() => {
                        return new Promise<void>(transfers_complete => {
                            if (transferDocument.Details.length > 0) {
                                console.log('Create & Release Transfers');
                                this.transfers.put(transferDocument, {
                                    loader: null
                                }).subscribe(newTransfer => {
                                    if (environment.release_pick_transfers) {
                                        this.transfers.Release(newTransfer).subscribe(() => {
                                            transfers_complete();
                                        })
                                    }
                                    transfers_complete();
                                }, error => { errHandler(error); return; });
                            } else {
                                transfers_complete();
                            }
                        });
                    })();
                    status.InventoryTransfers = CompleteShipmentStepStatus.Complete;
                }

            })().then(() => {
                subscriber.next();
                subscriber.complete();
            })
            // #endregion
        });
    }

    confirmShipment(shipment: Shipment, options: AcumaticaRequestOptions): Observable<void> {
        return this.acumatica.action('Shipment', 'ConfirmShipment', shipment, options);
    }

    // #endregion

    // // #region Suspended Shipments
    // get suspendedShipments(): { [key: string]: SuspendedShipment } {
    //     let suspendedShipmentsJSON = localStorage.getItem('suspended-shipments') || '{}';
    //     return JSON.parse(suspendedShipmentsJSON);
    // }
    // set suspendedShipments(value: { [key: string]: SuspendedShipment }) {
    //     localStorage.setItem('suspended-shipments', JSON.stringify(value));
    // }

    ReleaseFromHold(shipment: Shipment): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            if (shipment.Status != 'On Hold') { resolve(); return; }

            this.acumatica.action('Shipment', 'ReleaseFromHold', shipment)
                .subscribe(
                    () => { resolve(); },
                    (err) => reject(err)
                );
        });
    }

    serialize(shipment: Shipment): string {
        // clone the shipment object
        let clone = Object.assign(new Shipment(), shipment);
        // in order to serialize EventEmitters need deleted
        // also private member variables need de-privitized
        let deprivitize = (object: any, private_name: string, serialize_name: string) => {
            object[serialize_name] = object[private_name];
            delete object[private_name];
        }
        clone.Details.forEach(detail => {
            delete (<any>detail).AllocationsChanged;
            detail.Allocations.forEach((alloc, alloc_index) => {
                let oAlloc: any = Object.assign({}, alloc);
                delete oAlloc.QtyChanged;
                delete oAlloc.PickSource;
                deprivitize(oAlloc, '_qty', 'Qty');
                deprivitize(oAlloc, '_ADJ', 'ADJ');
                deprivitize(oAlloc, '_ADJQty', 'ADJQty');
                detail.Allocations[alloc_index] = oAlloc;
            })
        });

        return JSON.stringify(clone);
    }

    // suspend(shipment: Shipment, note?: string) {
    //     // references to suggested sources need reset as inventory data will be reloaded when resumed
    //     shipment.Details.forEach(detail => detail.SuggestedPickingSource = null);

    //     let serialized = this.serialize(shipment);

    //     let suspendedShipment: SuspendedShipment = {
    //         date: new Date().toISO8601(),
    //         data: serialized //JSON.stringify(shipment)
    //     }
    //     if (note) suspendedShipment.note = note;

    //     let suspended = this.suspendedShipments;
    //     suspended[shipment.ShipmentNbr] = suspendedShipment;
    //     this.suspendedShipments = suspended;
    // }

    // resumeSuspended(ShipmentNbr: string): Shipment {
    //     let suspendedShipment = this.suspendedShipments[ShipmentNbr];
    //     let shipment: Shipment = Object.assign(new Shipment(), JSON.parse(suspendedShipment.data));
    //     shipment.Details.forEach((detail, index_detail) => {
    //         shipment.Details[index_detail] = Object.assign(new ShipmentDetail(), detail);
    //         shipment.Details[index_detail].Allocations.forEach((alloc, index_alloc) => {
    //             shipment.Details[index_detail].Allocations[index_alloc] = Object.assign(new ShipmentDetailAllocation(), alloc);
    //         });
    //     });
    //     return shipment;
    // }

    // deleteSuspended(ShipmentNbr: string) {
    //     let suspended = this.suspendedShipments;
    //     delete suspended[ShipmentNbr];
    //     this.suspendedShipments = suspended;
    // }
    // // #endregion

    getSalesRepUnreceivedShipments(repWarehouseID: string): Observable<UnreceivedWarehouseTransfersDetail[]> {
        return new Observable<UnreceivedWarehouseTransfersDetail[]>(subscriber => {
            let genericInquiry = new GenericInquiryService(this.acumatica, 'UnreceivedWarehouseTransfers', UnreceivedWarehouseTransferInquiry);
            let inquiry = new AcumaticaEntity({ ToWarehouse: repWarehouseID });
            let options = { expand: 'UnreceivedWarehouseTransfersDetails', loader: null };

            genericInquiry.inquiry(inquiry, options).subscribe(result => {
                subscriber.next(result.UnreceivedWarehouseTransfersDetails);
                subscriber.complete();
            })
        });
    }
}

@Component({ template: '' })
export class ShipmentDetailAllocation extends AcumaticaEntity {
    @Acumatica() InventoryID: string | null | undefined = undefined;
    @Acumatica() LocationID: string | null = null;
    @Acumatica() LotSerialNbr: string | null = null;
    @Acumatica() UOM?: string;
    @Acumatica('Qty') private _qty: number = 0;

    private roundQty(value?: number): number | undefined | null {
        if (value === undefined || value === null || this._qtyRoundingPrecision == null) return value;
        return Math.roundTo(value, this._qtyRoundingPrecision);
    }

    constructor();
    constructor(detail?: ShipmentDetail)
    constructor(arg1?: ShipmentDetail) {
        super();
        if (arg1 !== undefined) this.QtyRoundingPrecision = arg1.QtyRoundingPrecision;
    }

    get Qty(): number { return this._qty; }
    set Qty(value: number) {
        value = this.roundQty(parseFloat(value.toString()) || 0)!;

        // adjust PickSource qty, plus previous value, minus new value
        if (this.PickSource !== undefined && value !== null) {
            let adjustment = (this._qty || 0)
                - (value || 0)
                - (this.PickSource.PreAllocated?.Quantity || 0);
            this.PickSource!.QtyAvailable = this.roundQty(this.PickSource!.QtyAvailable + adjustment)!;
            this.PickSource!.QtyAvailableForShipment = this.roundQty(this.PickSource!.QtyAvailableForShipment + adjustment)!;

        }
        if (this._qty != value) {
            this._qty = value;
            this.QtyChanged.emit();
        }
    }
    @Output() QtyChanged = new EventEmitter<number>();

    // Allowances variance
    EOR: boolean = false;
    EORQty: number | null = null;
    EORFree: boolean = true;

    PCS: boolean = false;
    PCSQty: number | null = null;

    FLA: boolean = false;
    FLAQty: number | null = null;
    FLAImgs: { file: File, src: SafeResourceUrl }[] = [];

    private _qtyRoundingPrecision: number | null = null;
    set QtyRoundingPrecision(value: number | null) {
        this._qtyRoundingPrecision = value;
    }

    /** the allocation quantity counting towards the order fulfilment, less extra allowances  */
    get fulfillmentQty(): number {
        return this.roundQty(this.Qty - this.allowanceQty)!;
    }

    /** the total quantity of any Allowances on the allocation */
    get allowanceQty(): number {
        // this.EORQty and this.FLAQty exists as string at runtime
        return this.roundQty(
            parseFloat(this.EOR ? (this.EORQty! || 0).toString() : '0')
            + parseFloat(this.PCS ? (this.PCSQty! || 0).toString() : '0')
            + parseFloat(this.FLA ? (this.FLAQty! || 0).toString() : '0')
        )!;
    }

    // Picking & Restocking
    PickSource?: InventorySummaryDetail;
    RestockQty: number = 0;
}

@Component({ template: '' })
export class ShipmentDetail extends AcumaticaEntity {
    @Acumatica() note?: string;
    @Acumatica() InventoryID!: string;
    @Acumatica() Description!: string;
    @Acumatica() LineNbr: number | null = null;
    @Acumatica({ write: false }) LotSerialNbr: string | null = null;
    @Acumatica({
        writer: (value, entity) => {
            let detail = <ShipmentDetail>entity;
            return detail.isTracked ? undefined : value;
        }
    }) LocationID: string | null = null;
    @Acumatica({ write: false }) OrderedQty: number = 0;
    @Acumatica({ write: false }) OpenQty: number = 0;
    @Acumatica({ write: false }) OriginalQty!: number;
    @Acumatica({ write: false }) ShippedQty: number = 0;
    @Acumatica() UOM!: string;
    @Acumatica() WarehouseID!: string;
    @Acumatica() OrderType!: string;
    @Acumatica() OrderNbr!: string;
    @Acumatica() OrderLineNbr!: number;
    @Acumatica({ write: false }) ItemTrackingMethod?: string;
    @Acumatica({ write: false }) LotSerialClassID?: string;
    @Acumatica() FreeItem?: boolean;
    @Acumatica({ write: false }) DefaultShipLocationPriority?: number;
    @Acumatica({ write: false }) DefaultShipLocationID?: string;

    @Acumatica(ShipmentDetailAllocation) Allocations: ShipmentDetailAllocation[] = [];

    private roundQty(value?: number): number | undefined | null {
        if (value === undefined || value === null || this.QtyRoundingPrecision == null) return value;
        return Math.roundTo(value, this.QtyRoundingPrecision);
    }

    // Pre-Allocated on the Sales Order
    PreAllocation?: PreAllocationDetail;

    _allocationQtyChangedSubscription?: Subscription;

    constructor() {
        super();
        let self = this;

        // override Allocations push method (used by Entity decoder when getting Shipment from Acumatica)
        // may contain SO-pre-allocated details
        (function (fn) {
            self.Allocations.push = (allocation: ShipmentDetailAllocation): number => {
                // provide the allocation the relevant rounding-precision
                allocation.QtyRoundingPrecision = self.QtyRoundingPrecision;

                // invoke original function to add to array
                let length = fn.apply(self.Allocations, [allocation]);
                // emit event to notify of allocation changes
                self.AllocationsChanged.emit();
                // subscribe to qty changed event of allocation detail
                allocation.QtyChanged.subscribe(self.AllocationsChanged);

                return length;
            }
        })(this.Allocations.push);
    }

    get QtyRoundingPrecision(): number { return (this.LotSerialClassID == 'R') ? 0.125 : 0.001; }

    get isTracked(): boolean { return this.ItemTrackingMethod != 'N'; }

    get SOKey(): string { return this.OrderType + '/' + this.OrderNbr; }

    // auto-suggested source for user to pick from
    SuggestedPickingSource: null | InventorySummaryDetail | string = null;
    get SuggestedPickingSourceLocation(): string {
        // if a string (i.e. "(PIECE)" or "(BACKORD)") return that
        if (typeof this.SuggestedPickingSource == 'string') return this.SuggestedPickingSource;
        // otherwise return the suggested Location ID or empty string if none
        return this.SuggestedPickingSource?.LocationID || '';
    }
    get SuggestedPickingSourceSerialLotNbr(): string {
        if (typeof this.SuggestedPickingSource == 'string') return '';
        return this.SuggestedPickingSource?.LotSerialNbr || '';
    }
    get SuggestedPickingSourceLocationID(): string {
        if (typeof this.SuggestedPickingSource == 'string') return '';
        return this.SuggestedPickingSource?.LocationID || '';
    }

    /** Provides a button in quick-pick form for actions like 'EOR' */
    QuickAction: QuickAction | null = null;

    /** Shipping Full Rolls -- Update SO to total qty shipped */
    ShipFullRolls: boolean = false;

    /** Flag causes pickingComplete to return false to allow adding to complete lines */
    forceInComplete: boolean = false;

    get pickingComplete(): boolean {
        if (this.forceInComplete) return false;

        // picking is complete when pick quanity is greater than or equal to
        // the order quantity, or in the case of sending full rolls 95% of the order qty
        return (this.PickedQty >= this.OrderedQty * (this.ShipFullRolls ? 0.95 : 1));
    }

    // #region Allocation collection within Shipment Detail

    /** emits event when new Allocations are added or removed from the Shipment Detail */
    @Output() AllocationsChanged = new EventEmitter();

    /**
     * total pick quantity for all allocations
     */
    get PickedQty(): number {
        let qty: number = 0;
        this.Allocations.forEach(alloc => qty = qty + (alloc.Qty || 0));
        return this.roundQty(qty)!;
    };

    DeleteAllocation(allocation: ShipmentDetailAllocation) {
        let idx = this.Allocations.indexOf(allocation);
        if (idx == -1) throw new Error('Allocation does not exist in collection');
        // release quantities to restore to InventorySummary.QtyAvailable
        allocation.Qty = 0;

        allocation.QtyChanged.unsubscribe();
        this.Allocations.splice(idx, 1)
        this.AllocationsChanged.emit();
    }

    NewAllocation(initialize?: any) {
        // Acumatica will create allocations for pre-assigned lines that should be used first
        if (this.PreAllocation && this.Allocations.length == 1 && this.Allocations[0].Qty == 0)
            return this.Allocations[0];

        let alloc = new ShipmentDetailAllocation();
        Object.assign(alloc, initialize);
        alloc.InventoryID = this.InventoryID;
        this.Allocations.push(alloc);
        return alloc;
    }
    // #endregion
}

export class ShipLineSplit extends AcumaticaEntity {
    @Acumatica() _id: string | null = null;
    @Acumatica() _delete: boolean = false;
    @Acumatica() LineNbr: number = -1;
    @Acumatica() SplitLineNbr: number = -1;
    @Acumatica() InventoryID: string | null = null;
    @Acumatica() LocationID: string | null = null;
    @Acumatica() LotSerialNbr: string | null = null;
    @Acumatica() Quantity: number = 0;
    @Acumatica() PickedQuantity: number = 0;
    @Acumatica() PackedQuantity: number = 0;
    @Acumatica() Released: boolean = false;
    @Acumatica() Confirmed: boolean = false;
}

export class PreAllocationDetail extends AcumaticaEntity {
    @Acumatica() LineNbr!: number;
    @Acumatica() LotSerialNbr!: string | null;
    @Acumatica() Quantity!: number;
    @Acumatica('Location') LocationID!: string | null;
}

export class ShipmentPackage extends AcumaticaEntity {
    @Acumatica() TrackingNbr!: string;
}

export class Shipment extends AcumaticaEntity {
    @Acumatica() Type!: string;
    @Acumatica() ShipmentNbr!: string;
    @Acumatica() CustomerID!: string;
    @Acumatica() WarehouseID!: string;
    @Acumatica() note?: string;
    @Acumatica() ToWarehouseID?: string;
    @Acumatica() Status?: string;
    @Acumatica() PickerID?: string | null;
    @Acumatica({ write: false }) PickerUsername?: string | null;
    @Acumatica({ format: AcumaticaFieldFormat.Date, write: false }) ShipmentDate?: Date;
    @Acumatica() DoNotAutoPrintPickList?: boolean;
    @Acumatica() CutAndHold?: boolean;

    @Acumatica(ShipmentDetail) Details: ShipmentDetail[] = [];

    @Acumatica(ShipmentPackage) Packages?: ShipmentPackage[];

    // @Acumatica(PreAllocationDetail)
    PreAllocations?: PreAllocationDetail[] = [];

    // #region Inventory Adjustments
    // array container for adjustments to apply
    adjustments: AdjustmentDetail[] = [];

    /** return a references to an existing AdjustmentDetail in the shipment's collection or undefined */
    getAdjustment(inventoryID: string, lotSerialNbr: string | null, locationID: string): AdjustmentDetail | undefined;
    getAdjustment(allocation: ShipmentDetailAllocation): AdjustmentDetail | undefined;
    getAdjustment(arg1: unknown, arg2?: unknown, arg3?: unknown): AdjustmentDetail | undefined {
        let inventoryID: string;
        let lotSerialNbr: string | null;
        let locationID: string;

        if (typeof arg1 === 'object') {
            let allocation = arg1 as ShipmentDetailAllocation;
            inventoryID = allocation.InventoryID!;
            lotSerialNbr = allocation.LotSerialNbr;
            locationID = allocation.LocationID!;
        } else {
            inventoryID = arg1 as string;
            lotSerialNbr = arg2 as string;
            locationID = arg3 as string;
        }

        return this.adjustments.find(adj =>
            adj.InventoryID == inventoryID &&
            adj.LotSerialNbr == lotSerialNbr &&
            adj.LocationID == locationID
        );
    }

    // initialize a new AdjustmentDetail and add to shipment's collection if needed and return a reference to it
    initAdjustment(inventoryID: string, lotSerialNbr: string, locationID: string): AdjustmentDetail;
    initAdjustment(allocation: ShipmentDetailAllocation): AdjustmentDetail;
    initAdjustment(arg1: unknown, arg2?: unknown, arg3?: unknown): AdjustmentDetail {
        let inventoryID: string;
        let lotSerialNbr: string | null;
        let locationID: string;

        if (typeof arg1 === 'object') {
            let allocation = arg1 as ShipmentDetailAllocation;
            inventoryID = allocation.InventoryID!;
            lotSerialNbr = allocation.LotSerialNbr;
            locationID = allocation.LocationID!;
        } else {
            inventoryID = arg1 as string;
            lotSerialNbr = arg2 as string;
            locationID = arg3 as string;
        }

        let adj = this.getAdjustment(inventoryID, lotSerialNbr, locationID);
        if (adj === undefined) {
            adj = new AdjustmentDetail();
            adj.InventoryID = inventoryID;
            adj.LotSerialNbr = lotSerialNbr;
            adj.LocationID = locationID;
            adj.WarehouseID = this.WarehouseID!;
            this.adjustments.push(adj);
        }
        return adj;
    }

    // delete an adjustment from the shipment's adjustments collection
    deleteAdjustment(adjustment: AdjustmentDetail) {
        this.adjustments.remove(adjustment);
    }

    // #endregion

    get pickingComplete(): boolean {
        let pickingComplete = true;
        this.Details.forEach(detail => pickingComplete &&= detail.pickingComplete);
        return pickingComplete;
    }

    // return the ShipmentDetail object corresponding to the given ShipLineSplit or Shipment LineNbr
    DetailLine(Line: number | ShipLineSplit): ShipmentDetail | undefined {
        if (Line instanceof ShipLineSplit) Line = Line.LineNbr;
        return this.Details.find(detail => detail.LineNbr == Line);
    }
}

export class UnreceivedWarehouseTransfersDetail extends AcumaticaEntity {
    @Acumatica({ format: AcumaticaFieldFormat.Date }) ShipmentDate!: Date;
    @Acumatica() ShipmentNbr!: string;
    @Acumatica() WarehouseID!: string;
    @Acumatica() ToWarehouse!: string;
}

export class UnreceivedWarehouseTransferInquiry extends AcumaticaEntity {
    @Acumatica() ToWarehouse!: string;
    @Acumatica(UnreceivedWarehouseTransfersDetail) UnreceivedWarehouseTransfersDetails?: UnreceivedWarehouseTransfersDetail[];
}

interface QuickAction {
    label: string,
    action: () => void
}

export enum CompleteShipmentStepStatus {
    Omitted,
    Pending,
    InProcess,
    Complete,
    Error
}

@Component({ template: '' })
export class CompleteShipmentStatus {
    @Output() updated: EventEmitter<CompleteShipmentStatus> = new EventEmitter();

    private statusUpdated(resetDetails = true) {
        if (resetDetails) this._Details = null;
        this.updated.emit(this);
    }

    private _error: any = undefined;
    get error(): any { return this._error; }
    set error(error: any) {
        this._error = error;

        // updating statuses will reset Details -- retain it
        let details = this._Details;
        if (this.InventoryAdjustments == CompleteShipmentStepStatus.InProcess) this.InventoryAdjustments = CompleteShipmentStepStatus.Error;
        if (this.ModifySOLines == CompleteShipmentStepStatus.InProcess) this.ModifySOLines = CompleteShipmentStepStatus.Error;
        if (this.AppendSOLines == CompleteShipmentStepStatus.InProcess) this.AppendSOLines = CompleteShipmentStepStatus.Error;
        if (this.GetModifiedSOs == CompleteShipmentStepStatus.InProcess) this.GetModifiedSOs = CompleteShipmentStepStatus.Error;
        if (this.AppendShipmentLines == CompleteShipmentStepStatus.InProcess) this.AppendShipmentLines = CompleteShipmentStepStatus.Error;
        if (this.UpdateShipment == CompleteShipmentStepStatus.InProcess) this.UpdateShipment = CompleteShipmentStepStatus.Error;
        if (this.PrintingLabels == CompleteShipmentStepStatus.InProcess) this.PrintingLabels = CompleteShipmentStepStatus.Error;
        if (this.PrintPacklist == CompleteShipmentStepStatus.InProcess) this.PrintPacklist = CompleteShipmentStepStatus.Error;
        if (this.UploadImages == CompleteShipmentStepStatus.InProcess) this.UploadImages = CompleteShipmentStepStatus.Error;
        if (this.InventoryTransfers == CompleteShipmentStepStatus.InProcess) this.InventoryTransfers = CompleteShipmentStepStatus.Error;

        this.Details = details;
    }

    private _Details: string | null = null;
    get Details() { return this._Details; }
    set Details(value: string | null) { this._Details = value; this.statusUpdated(false); }

    private _InventoryAdjustments: CompleteShipmentStepStatus = CompleteShipmentStepStatus.Omitted;
    get InventoryAdjustments() { return this._InventoryAdjustments; }
    set InventoryAdjustments(value) { this._InventoryAdjustments = value; this.statusUpdated(); }

    private _ModifySOLines: CompleteShipmentStepStatus = CompleteShipmentStepStatus.Omitted;
    get ModifySOLines() { return this._ModifySOLines; }
    set ModifySOLines(value) { this._ModifySOLines = value; this.statusUpdated(); }

    private _AppendSOLines: CompleteShipmentStepStatus = CompleteShipmentStepStatus.Omitted;
    get AppendSOLines() { return this._AppendSOLines; }
    set AppendSOLines(value) { this._AppendSOLines = value; this.statusUpdated(); }

    private _GetModifiedSOs: CompleteShipmentStepStatus = CompleteShipmentStepStatus.Omitted;
    get GetModifiedSOs() { return this._GetModifiedSOs; }
    set GetModifiedSOs(value) { this._GetModifiedSOs = value; this.statusUpdated(); }

    private _AppendShipmentLines: CompleteShipmentStepStatus = CompleteShipmentStepStatus.Omitted;
    get AppendShipmentLines() { return this._AppendShipmentLines; }
    set AppendShipmentLines(value) { this._AppendShipmentLines = value; this.statusUpdated(); }

    private _UpdateShipment: CompleteShipmentStepStatus = CompleteShipmentStepStatus.Pending;
    get UpdateShipment() { return this._UpdateShipment; }
    set UpdateShipment(value) { this._UpdateShipment = value; this.statusUpdated(); }

    private _UploadImages: CompleteShipmentStepStatus = CompleteShipmentStepStatus.Omitted;
    get UploadImages() { return this._UploadImages; }
    set UploadImages(value) { this._UploadImages = value; this.statusUpdated(); }

    private _PrintingLabels: CompleteShipmentStepStatus = CompleteShipmentStepStatus.Omitted;
    get PrintingLabels() { return this._PrintingLabels; }
    set PrintingLabels(value) { this._PrintingLabels = value; this.statusUpdated(); }

    private _InventoryTransfers: CompleteShipmentStepStatus = CompleteShipmentStepStatus.Omitted;
    get InventoryTransfers() { return this._InventoryTransfers; }
    set InventoryTransfers(value) { this._InventoryTransfers = value; this.statusUpdated(); }

    private _PrintPacklist: CompleteShipmentStepStatus = CompleteShipmentStepStatus.Omitted;
    get PrintPacklist() { return this._PrintPacklist; }
    set PrintPacklist(value) { this._PrintPacklist = value; this.statusUpdated(); }

    constructor(shipment: Shipment) {
        this.UploadImages =
            shipment.Details.filter(detail => detail.Allocations.filter(alloc => alloc.FLAImgs.length > 0).length > 0).length
                ? CompleteShipmentStepStatus.Pending
                : CompleteShipmentStepStatus.Omitted;

        this.InventoryAdjustments = shipment.adjustments.filter(adjustment => adjustment.Quantity != 0).length
            ? CompleteShipmentStepStatus.Pending
            : CompleteShipmentStepStatus.Omitted;

        this.ModifySOLines =
            shipment.Details.filter(detail => detail.ShipFullRolls).length
                ? CompleteShipmentStepStatus.Pending
                : CompleteShipmentStepStatus.Omitted;

        this.AppendShipmentLines =
            this.AppendSOLines =
            shipment.Details.filter(detail => detail.Allocations.filter(alloc => alloc.EORQty != 0 || alloc.PCSQty != 0 || alloc.FLAQty != 0).length).length
                ? CompleteShipmentStepStatus.Pending
                : CompleteShipmentStepStatus.Omitted;

        this.GetModifiedSOs = (this.ModifySOLines == CompleteShipmentStepStatus.Pending || this.AppendSOLines == CompleteShipmentStepStatus.Pending)
            ? CompleteShipmentStepStatus.Pending
            : CompleteShipmentStepStatus.Omitted;

        this.PrintingLabels = CompleteShipmentStepStatus.Omitted;

        this.InventoryTransfers =
            shipment.Details.filter(detail => detail.Allocations.filter(alloc => alloc.PickSource !== undefined && alloc.PickSource?.LocationID != alloc.PickSource?.OriginalLocationID).length).length
                ? CompleteShipmentStepStatus.Pending
                : CompleteShipmentStepStatus.Omitted;
    }

    public toString() {
        if (this.error) return "ERROR!";
        if (this.InventoryAdjustments == CompleteShipmentStepStatus.InProcess) return "Adjusting Inventory";
        if (this.ModifySOLines == CompleteShipmentStepStatus.InProcess) return "Modifying SO Lines";
        if (this.AppendSOLines == CompleteShipmentStepStatus.InProcess) return "Adding Allowances to SO";
        if (this.GetModifiedSOs == CompleteShipmentStepStatus.InProcess) return "Retrieving Modified SO";
        if (this.AppendShipmentLines == CompleteShipmentStepStatus.InProcess) return "Adding Allowances to Shipment";
        if (this.UpdateShipment == CompleteShipmentStepStatus.InProcess) return "Updating Shipment";
        if (this.PrintingLabels == CompleteShipmentStepStatus.InProcess) return "Printing Labels";
        if (this.PrintPacklist == CompleteShipmentStepStatus.InProcess) return "Printing Pack List";
        if (this.UploadImages == CompleteShipmentStepStatus.InProcess) return "Uploading Images";
        if (this.InventoryTransfers == CompleteShipmentStepStatus.InProcess) return "Creating Location Transfers";
        return "Completed";
    }
}

export interface SuspendedShipment {
    date: string,
    data: string,
    note?: string,
}